import React, { useRef, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useFormik } from 'formik';

import { Modal, ModalBodyFooter, Button, ModalLoading, ErrorMessage } from '../../custom-essentials';
import { SelectSingle, SelectMulti, FormikControl, Check } from '../../form';
import { TooltipWrapper } from '../../display';
import {
    readingValidationSchema,
    readingAssessmentOptions,
    renderReadingBookFields,
    getSelectedInstructors,
    statusOptions,
    getInitSelectedStatus,
    getCenterOptions,
    getInitSelectedCenter,
    renderSubmitting,
    renderRelevantNotes,
    renderPastFlags
} from './helpers';
import { tagOptions as flagTags } from '../flags/helpers';
import { formatDateFull, formatTime } from '../../functions';

function DetailsBodyFooter(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });

    const { loaded, attemptingClose, setAttemptingClose, centers,
        instructorOptions, selectedAppointment, relInfo,
        relevantAssignments, submitted, submissionStatus, handleClose, handleSubmit,
        bookListOptions = [] } = props;

    const defaultBookListOption = (bookListOptions && bookListOptions.length > 0) ? bookListOptions[0] : { value: -1, label: 'No book lists found', obj: {} };
    const getReadingAssessmentValue = (value) => {
        const numericValue = parseInt(value || 0);
        return readingAssessmentOptions.find(opt => opt.value === numericValue) || readingAssessmentOptions[0];
    };

    // Get initial values - memoized to avoid recreating on every render
    const initialValues = useMemo(() => ({
        instructors: getSelectedInstructors(relevantAssignments || [], instructorOptions || []),
        status: getInitSelectedStatus(selectedAppointment?.status),
        participation: getReadingAssessmentValue(selectedAppointment?.participation),
        answerAccuracy: getReadingAssessmentValue(selectedAppointment?.answer_accuracy),
        depthOfThinking: getReadingAssessmentValue(selectedAppointment?.depth_of_thinking),
        vocabPages: selectedAppointment?.vocab_pages ?? '',
        sessionNotes: selectedAppointment?.session_notes ?? '',
        hwNotes: selectedAppointment?.homework_notes ?? '',
        internalNotes: selectedAppointment?.internal_notes ?? '',
        stampsGiven: parseInt(selectedAppointment?.stamps_given || 0) === 1,
        specialNotesSchedule: selectedAppointment?.special_notes_schedule ?? '',
        bookListOptions: bookListOptions || [],
        selectedBookList: defaultBookListOption,
        hideCompletedBooks: true,
        createFlag: false,
        flagTag: { value: -1, label: 'Please select...' },
        flagCenter: getInitSelectedCenter(selectedAppointment?.center, centers),
        flagCenterUnlock: false,
        flagNotes: ''
    }), [relevantAssignments, instructorOptions, selectedAppointment, bookListOptions, centers, defaultBookListOption]);

    // Use useFormik hook - must be called before any conditional returns
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: readingValidationSchema,
        onSubmit: handleSubmit
    });

    // Handle formik reinitialization when props change
    useEffect(() => {
        if(loaded && selectedAppointment && instructorOptions && instructorOptions.length > 0){
            const defaultBookListOption = (bookListOptions && bookListOptions.length > 0) ? bookListOptions[0] : { value: -1, label: 'No book lists found', obj: {} };
            formik.setValues({
                instructors: getSelectedInstructors(relevantAssignments || [], instructorOptions || []),
                status: getInitSelectedStatus(selectedAppointment?.status),
                participation: getReadingAssessmentValue(selectedAppointment?.participation),
                answerAccuracy: getReadingAssessmentValue(selectedAppointment?.answer_accuracy),
                depthOfThinking: getReadingAssessmentValue(selectedAppointment?.depth_of_thinking),
                vocabPages: selectedAppointment?.vocab_pages ?? '',
                sessionNotes: selectedAppointment?.session_notes ?? '',
                hwNotes: selectedAppointment?.homework_notes ?? '',
                internalNotes: selectedAppointment?.internal_notes ?? '',
                stampsGiven: parseInt(selectedAppointment?.stamps_given || 0) === 1,
                specialNotesSchedule: selectedAppointment?.special_notes_schedule ?? '',
                bookListOptions: bookListOptions || [],
                selectedBookList: defaultBookListOption,
                hideCompletedBooks: true,
                createFlag: false,
                flagTag: { value: -1, label: 'Please select...' },
                flagCenter: getInitSelectedCenter(selectedAppointment?.center, centers),
                flagCenterUnlock: false,
                flagNotes: ''
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAppointment, instructorOptions, relevantAssignments, bookListOptions, centers, loaded]);

    if (!loaded) {
        return (
            <ModalBodyFooter>
                <Modal.Body>
                    <ModalLoading />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        color="lte-mpLRed"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </ModalBodyFooter>
        );
    } else if (submitted) {
        return (
            <ModalBodyFooter>
                <Modal.Body>
                    <h4>Updated successfully!</h4>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        color="lte-mpLRed"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </ModalBodyFooter>
        );
    } else if(formik.isSubmitting){
        return renderSubmitting(submissionStatus, formik.setSubmitting);
    } else if(attemptingClose){
        return (
                            <ModalBodyFooter>
                                <Modal.Body>
                                    <h4>Are you sure you would like to close the form?</h4>
                                </Modal.Body>
                                <Modal.Footer>
                                    <div className="flex flex-row gap-x-2 flex-wrap">
                                        <Button
                                            color="lte-mpLRed"
                                            onClick={handleClose}
                                            style={{ marginRight: '5px' }}
                                        >
                                            Close Form
                                        </Button>
                                        <Button
                                            color="lte-mpLBlue"
                                            onClick={() => setAttemptingClose(false)}
                                        >
                                            Back to Form
                                        </Button>
                                    </div>
                                </Modal.Footer>
                            </ModalBodyFooter>
        );
    } else {
        return (
            <ModalBodyFooter>
                <Modal.Body>

                                    <h2>Scheduling</h2>
                                    <br />
                                    <div className="flex flex-row gap-x-4">
                                        <div className="w-1/2">
                                            <div className="grid grid-cols-1 gap-y-2">
                                                <h4>Appointment Info</h4>
                                                <div className="ml-1 text-sm">
                                                    <div><b>Student:</b> {selectedAppointment.studentName}</div>
                                                    <div><b>Time:</b>&nbsp;
                                                        {formatDateFull(selectedAppointment.date_time)}&nbsp;
                                                        from {formatTime(selectedAppointment.startTime)}&nbsp;
                                                        to {formatTime(parseInt(selectedAppointment.endTime))}
                                                    </div>
                                                    <div><b>Center:</b> {selectedAppointment.centerName}</div>
                                                </div>
                                            </div>

                                            <br />

                                            <div className="grid grid-cols-1 gap-y-2">
                                                <h4>Status</h4>
                                                <SelectSingle
                                                    id="details-status"
                                                    name="status"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.status}
                                                    options={statusOptions}
                                                />
                                                {formik.errors.status && (
                                                    <ErrorMessage color="mpLRed">
                                                        {formik.errors.status}
                                                    </ErrorMessage>
                                                )}
                                            </div>
                                        </div>
                                        <div className="w-1/2">
                                            <h4>Instructors</h4>
                                            <SelectMulti
                                                id="details-instructors"
                                                name="instructors"
                                                onChange={(e) => {
                                                    if (e.target.value.length <= 5) formik.handleChange(e);
                                                }}
                                                value={formik.values.instructors}
                                                options={instructorOptions}
                                            />
                                            {formik.values.instructors.length >= 5 && (
                                                <>
                                                    <br />
                                                    <div className="text-mpOrange">Maximum 5 instructors</div>
                                                </>
                                            )}

                                            <br />

                                            <TooltipWrapper
                                                tooltipText={
                                                    <div>
                                                        This will update the scheduling notes for the selected appointment.
                                                        The icon for scheduling notes is <i className="fas fa-exclamation text-mpGreen" />
                                                    </div>
                                                }
                                            >
                                                <h4 className="text-mpLBlue">Scheduling Notes</h4>
                                            </TooltipWrapper>
                                            <FormikControl
                                                id="details-special-notes-schedule"
                                                as="textarea"
                                                name="specialNotesSchedule"
                                                onChange={formik.handleChange}
                                                value={formik.values.specialNotesSchedule}
                                                placeholder="This will update the scheduling notes (green !) for this appointment. Max 250 characters"
                                            />
                                            {formik.errors.specialNotesSchedule && (
                                                <ErrorMessage color="mpLRed">
                                                    {formik.errors.specialNotesSchedule}
                                                </ErrorMessage>
                                            )}
                                        </div>
                                    </div>

                                    <br />
                                    <hr />
                                    <br />

                                    <h2>Progress Report</h2>
                                    <br />
                                    <div className="flex flex-row gap-x-4">
                                        <div className="w-1/2">
                                            <div className="grid grid-cols-1 gap-y-2">
                                                <h4>Participation</h4>
                                                <SelectSingle
                                                    id="details-participation"
                                                    name="participation"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.participation}
                                                    options={readingAssessmentOptions}
                                                />
                                                {formik.errors.participation && (
                                                    <ErrorMessage color="mpLRed">
                                                        {formik.errors.participation}
                                                    </ErrorMessage>
                                                )}
                                            </div>

                                            <br />

                                            <div className="grid grid-cols-1 gap-y-2">
                                                <h4>Accuracy</h4>
                                                <SelectSingle
                                                    id="details-accuracy"
                                                    name="answerAccuracy"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.answerAccuracy}
                                                    options={readingAssessmentOptions}
                                                />
                                                {formik.errors.answerAccuracy && (
                                                    <ErrorMessage color="mpLRed">
                                                        {formik.errors.answerAccuracy}
                                                    </ErrorMessage>
                                                )}
                                            </div>

                                            <br />

                                            <div className="grid grid-cols-1 gap-y-2">
                                                <h4>Depth of Thinking</h4>
                                                <SelectSingle
                                                    id="details-depth"
                                                    name="depthOfThinking"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.depthOfThinking}
                                                    options={readingAssessmentOptions}
                                                />
                                                {formik.errors.depthOfThinking && (
                                                    <ErrorMessage color="mpLRed">
                                                        {formik.errors.depthOfThinking}
                                                    </ErrorMessage>
                                                )}
                                            </div>

                                            <br />

                                            <div className="grid grid-cols-1 gap-y-2">
                                                <h4>Reading / Vocab Progress</h4>
                                                <FormikControl
                                                    id="details-vocab-pages"
                                                    name="vocabPages"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.vocabPages}
                                                    placeholder="Completed pages, books, or vocab focus"
                                                />
                                                {formik.errors.vocabPages && (
                                                    <ErrorMessage color="mpLRed">
                                                        {formik.errors.vocabPages}
                                                    </ErrorMessage>
                                                )}
                                            </div>

                                            <br />

                                            <div className="grid grid-cols-1 gap-y-2">
                                                <Check
                                                    id="details-stamps-given"
                                                    name="stampsGiven"
                                                    color="mpLRed"
                                                    label="Stamps Given?"
                                                    checked={formik.values.stampsGiven}
                                                    onChange={formik.handleChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="w-1/2">
                                            <div className="grid grid-cols-1 gap-y-2 w-full">
                                                <TooltipWrapper tooltipText="Session notes can be viewed by the parent.">
                                                    <h4 className="text-mpLBlue">Session Notes</h4>
                                                </TooltipWrapper>
                                                <FormikControl
                                                    id="details-session-notes"
                                                    name="sessionNotes"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.sessionNotes}
                                                    as="textarea"
                                                    placeholder="Notes to be shared with parents. Max 1,500 characters"
                                                />
                                                {formik.errors.sessionNotes && (
                                                    <ErrorMessage color="mpLRed">
                                                        {formik.errors.sessionNotes}
                                                    </ErrorMessage>
                                                )}
                                            </div>

                                            <br />

                                            <div className="grid grid-cols-1 gap-y-2 w-full">
                                                <h4>Homework Notes</h4>
                                                <FormikControl
                                                    id="details-homework-notes"
                                                    name="hwNotes"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.hwNotes}
                                                    as="textarea"
                                                    placeholder="Updates when homework is completed during the session. Max 300 characters"
                                                />
                                                {formik.errors.hwNotes && (
                                                    <ErrorMessage color="mpLRed">
                                                        {formik.errors.hwNotes}
                                                    </ErrorMessage>
                                                )}
                                            </div>

                                            <br />

                                            <div className="grid grid-cols-1 gap-y-2 w-full">
                                                <h4>Internal Notes</h4>
                                                <FormikControl
                                                    id="details-internal-notes"
                                                    name="internalNotes"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.internalNotes}
                                                    as="textarea"
                                                    placeholder="Instructor-only context. Max 500 characters"
                                                />
                                                {formik.errors.internalNotes && (
                                                    <ErrorMessage color="mpLRed">
                                                        {formik.errors.internalNotes}
                                                    </ErrorMessage>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <br />
                                    <hr />
                                    <br />

                                    <h2>Books &amp; Notes</h2>
                                    <br />
                                    <div className="flex flex-row gap-x-4">
                                        <div className="w-1/2">
                                            <div className="grid grid-cols-1 gap-y-2">
                                                <h4>Book Lists</h4>
                                                <SelectSingle
                                                    id="details-selected-booklist"
                                                    name="selectedBookList"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.selectedBookList}
                                                    options={bookListOptions}
                                                />
                                                <div className="flex flex-row gap-x-4">
                                                    <TooltipWrapper tooltipText="Hide books that were already marked completed when the form was opened.">
                                                        <Check
                                                            id="details-hideCompletedBooks"
                                                            name="hideCompletedBooks"
                                                            color="mpLRed"
                                                            label={<div className="text-mpLBlue">Hide Completed</div>}
                                                            checked={formik.values.hideCompletedBooks}
                                                            onChange={formik.handleChange}
                                                        />
                                                    </TooltipWrapper>
                                                </div>
                                                {renderReadingBookFields(formik)}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                            {renderRelevantNotes(relInfo.combinedNotes)}
                                        </div>
                                    </div>

                                    <br />
                                    <hr />
                                    <br />

                                    <h2>Flags</h2>
                                    <br />
                                    <div className="flex flex-row gap-x-4">
                                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                            <div className="grid grid-cols-1 gap-y-2 w-full">
                                                <Check
                                                    id="details-create-flag"
                                                    name="createFlag"
                                                    color="mpLRed"
                                                    label="Create Flag"
                                                    checked={formik.values.createFlag}
                                                    onChange={formik.handleChange}
                                                />
                                            </div>
                                            {formik.values.createFlag && (
                                                <div className="grid grid-cols-1 gap-y-2 w-full">
                                                    <h4>Tag</h4>
                                                    <SelectSingle
                                                        id="details-flag-tag"
                                                        name="flagTag"
                                                        onChange={formik.handleChange}
                                                        value={formik.values.flagTag}
                                                        options={flagTags}
                                                    />
                                                    {formik.errors.flagTagValid && (
                                                        <ErrorMessage color="mpLRed">
                                                            {formik.errors.flagTagValid}
                                                        </ErrorMessage>
                                                    )}

                                                    <br />

                                                    <h4>Center</h4>
                                                    <SelectSingle
                                                        id="details-flag-center"
                                                        name="flagCenter"
                                                        disabled={!formik.values.flagCenterUnlock}
                                                        onChange={formik.handleChange}
                                                        value={formik.values.flagCenter}
                                                        options={getCenterOptions(centers)}
                                                    />
                                                    <div className="text-mpOrange">
                                                        Center defaults to the center that this appointment was at.
                                                    </div>
                                                    {formik.errors.flagCenterValid && (
                                                        <ErrorMessage color="mpLRed">
                                                            {formik.errors.flagCenterValid}
                                                        </ErrorMessage>
                                                    )}
                                                    <Check
                                                        id="details-flag-center-unlocked"
                                                        name="flagCenterUnlock"
                                                        color="mpLRed"
                                                        label="Change Center"
                                                        checked={formik.values.flagCenterUnlock}
                                                        onChange={formik.handleChange}
                                                    />

                                                    <br />

                                                    <h4>Flag Notes</h4>
                                                    <FormikControl
                                                        as="textarea"
                                                        id="details-flag-notes"
                                                        name="flagNotes"
                                                        onChange={formik.handleChange}
                                                        value={formik.values.flagNotes}
                                                        placeholder="Extra comments about the flag in case the tag doesn't suffice. Max 200 characters"
                                                    />
                                                    {formik.errors.flagNotes && (
                                                        <ErrorMessage color="mpLRed">
                                                            {formik.errors.flagNotes}
                                                        </ErrorMessage>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 gap-y-2 w-1/2">
                                            {formik.values.createFlag && renderPastFlags(relInfo.flags)}
                                        </div>
                                    </div>
                                </Modal.Body>
                                <Modal.Footer>
                                    <div className="flex flex-row gap-x-2 flex-wrap">
                                        {!formik.isValid && parseInt(formik.submitCount) && !formik.isSubmitting ? (
                                            <div className="text-mpLRed">One or more fields needs to be corrected.</div>
                                        ) : null}
                                        {formik.status && !formik.isSubmitting ?
                                            <div className="text-mpLRed">{formik.status}</div> : null
                                        }
                                        <Button
                                            color="lte-mpLRed"
                                            disabled={formik.isSubmitting}
                                            onClick={handleClose}
                                        >
                                            Close
                                        </Button>
                                        <Button
                                            color="lte-mpLBlue"
                                            onClick={formik.handleSubmit}
                                            disabled={formik.isSubmitting}
                                        >
                                            Submit
                                        </Button>
                                        <Button
                                            color="lte-mpLBlue"
                                            onClick={(e) => {
                                                formik.setFieldValue('status', { value: 'Completed', label: 'Completed' });
                                                formik.handleSubmit(e);
                                            }}
                                            disabled={formik.isSubmitting}
                                        >
                                            Mark Completed &amp; Submit
                                        </Button>
                                    </div>
                                </Modal.Footer>
            </ModalBodyFooter>
        );
    }
}

// Set displayName and _modalComponentType so findComponentByType can find this component
DetailsBodyFooter.displayName = 'BodyFooter';
DetailsBodyFooter._modalComponentType = 'BodyFooter';

export default connect(null, {

})(DetailsBodyFooter);
